import BaseChart from './base-chart';
import { service } from '@ember/service';
import { computed } from '@ember/object';
import Consts from '../../utils/constants';

import cloneDeep from 'lodash.clonedeep';

export default class NoiseChart extends BaseChart {
    @service('data') model;
    @service errorHandling;
    @service tabService;
    @service pdfExport;
    @service intl;
    @service toolData;
    @service plotData;
    @service plotDataService;
    @service selectionData;
    @service componentManager;

    ampLine = [];
    adcLine = [];
    systemLine = [];
    sourceLine = [];
    frequencyLine = [];

    minY = 0;
    maxY = 0;

    ticks = [];

    fixedTicks = {};
    fixedMinY = {};
    fixedMaxY = {};

    labels = [];
    seriesColors = [];
    series = [];
    graphLines = [];

    sourceCalculated = false;

    init() {
        super.init();

        this.fixedTicks = cloneDeep(this.plotData.defaultNoiseObject);
        this.fixedMinY = cloneDeep(this.plotData.defaultNoiseObject);
        this.fixedMaxY = cloneDeep(this.plotData.defaultNoiseObject);

        this.set('pdfExport.tabs.noiseChart', this);
    }

    didRender() {
        super.didRender();
        this.draw();
    }

    @computed('chartId') get chartType() {
        return this.chartId.replace('Chart', '');
    }

    _resetPlot(plotData) {
        this.setProperties({
            ampLine: [],
            adcLine: [],
            systemLine: [],
            sourceLine: [],
            frequencyLine: [],
            ticks: [],
            labels: [],
            seriesColors: [],
            series: [],
            graphLines: [],
            minY: plotData.amp[0],
            maxY: plotData.amp[0],
            sourceCalculated: false,
        });
    }

    _resetGraphObjects() {
        let intl = this.intl,
            graphObjects = this.plotData.noiseDistortionGraphObjects;

        Object.keys(graphObjects).forEach((key) => {
            let graphObject = graphObjects[key];
            if (!graphObject.required || this.model[graphObject.required]) {
                this.labels.push(intl.t(graphObject.translation).toString());
                this.seriesColors.push(this.toolData.data.colors[graphObject.color]);
                this.series.push(graphObject.series);
                this.graphLines.push(this[graphObject.graphLine]);
            }
        });

        if (window.$(`#${this.chartId}`)) {
            window.$(`#${this.chartId}`).empty();
        }
    }

    _removeTicksOutsidePlotScope(frequency, xAxisValues, plotData) {
        let xAxisEnd = xAxisValues[xAxisValues.length - 1][0];

        if (Math.floor(frequency[frequency.length - 1]) > xAxisEnd) {
            let removeIndex = -1;

            for (let i = 0; i < frequency.length; i++) {
                if (frequency[i] > xAxisEnd) {
                    removeIndex = i;
                    break;
                }
            }

            if (removeIndex !== -1) {
                // Remove all values that are not being shown on plot.
                frequency.splice(removeIndex);
                plotData.amp.splice(removeIndex);
                plotData.adc.splice(removeIndex);
                plotData.system.splice(removeIndex);
                if (!this.sourceCalculated) plotData.source.splice(removeIndex);
            }
        }
    }

    _generateTicks(plotData) {
        // Generate the y-axis tick marks.
        let count = plotData.getStartingPoint(this.minY);
        while (count <= this.maxY) this.ticks.push((count += plotData.tickIncrement));

        // So that there is some top margin
        if (count - this.maxY < plotData.requiredYPadding) this.ticks.push((count += plotData.tickIncrement));

        if (this.ticks.length > 0) {
            this.minY = this.ticks[0];
            this.maxY = this.ticks[this.ticks.length - 1];
        }
    }

    _calculateScale(...scalingGroup) {
        let tempMin = Math.min(...scalingGroup),
            tempMax = Math.max(...scalingGroup);

        if (this.minY > tempMin) this.minY = tempMin;
        if (this.maxY < tempMax) this.maxY = tempMax;
    }

    _scale(plotData) {
        let _this = this,
            type = this.type,
            enableDisabledPlot =
                Array.isArray(this.fixedTicks[type]) &&
                !this.fixedTicks[type].length &&
                Array.isArray(this.ticks) &&
                this.ticks.length;

        if (!plotData.zoomFitAllowed || this.model.zoomFitScaling || enableDisabledPlot) {
            this.fixedMinY[type] = this.minY;
            this.fixedMaxY[type] = this.maxY;
            this.fixedTicks[type] = this.ticks;

            if (plotData.zoomFitAllowed) {
                // This round of scaling should be completed
                this.set('model.zoomFitScaling', false);
                this.set('model.showZoomFit', false);
            }
        } else if (
            !(
                this.fixedTicks[type].length === this.ticks.length &&
                this.fixedTicks[type].every(function (v, i) {
                    return v === _this.ticks[i];
                })
            )
        ) {
            this.set('model.showZoomFit', true);
        } else {
            this.set('model.showZoomFit', false);
        }
    }

    _generatePlotLines(plotData, frequency, start, end) {
        this._resetPlot(plotData);

        if (!Array.isArray(plotData.source)) {
            // Convert single point to a graph line
            this.sourceLine = [
                [start, plotData.source],
                [end, plotData.source],
            ];
            this.sourceCalculated = true;
        }

        for (let i = 0; i < frequency.length; i++) {
            let x = plotData.amp[i],
                y = plotData.adc[i],
                z = plotData.system[i],
                a = plotData.source[i],
                scalingGroup = this.model.showOptionalSourceSettings ? [x, y, z, a] : [x, y, z];

            this._calculateScale(...scalingGroup);

            this.ampLine[i] = [frequency[i], x];
            this.adcLine[i] = [frequency[i], y];
            this.systemLine[i] = [frequency[i], z];
            if (!this.sourceCalculated) this.sourceLine[i] = [frequency[i], a];
        }

        this._generateTicks(plotData);

        this._scale(plotData);

        let indexAtFreq = this.plotDataService.noiseArrays.indexAtFreq,
            type = this.type;
        this.frequencyLine = [
            [frequency[indexAtFreq], this.fixedMinY[type]],
            [frequency[indexAtFreq], this.fixedMaxY[type]],
        ];
    }

    draw(force = false) {
        let intl = this.intl,
            selfRef = this,
            plotData = this.plotDataService.noiseArrays.noisePlotData[this.type];

        if (
            !force &&
            (this.errorHandling.tabError ||
                this.errorHandling.isNoiseSimInputsError ||
                this.errorHandling.vSupplyError ||
                this.errorHandling.isSettlingNoiseError ||
                this.errorHandling.isNoiseSimInputsError ||
                this.errorHandling.disabledTHDTabError)
        )
            return;

        let chartElement = document.querySelector(`#${this.chartId}`);

        // Make sure the chart container is there and has width before we initialize the chart with jqplot
        if (!(force || (chartElement && chartElement.offsetWidth))) return;

        let yTitle = intl.t(`chart_labels.${this.type}`),
            frequency = this.plotDataService.noiseArrays.frequencyArray,
            // Get the start and end values of the x axis.
            start = frequency[0],
            end = Math.min(this.model.samplingRate * 0.5, this.selectionData.selectedAdc.freqMax),
            // Get the log tick marks for the x-axis.
            xAxisValues = this.setLogTicks(start, end);

        this._removeTicksOutsidePlotScope(frequency, xAxisValues, plotData);

        this._generatePlotLines(plotData, frequency, start, end);

        this._resetGraphObjects();

        let plot = window.jQuery.jqplot(this.chartId, this.graphLines, {
            height: 450,
            axesDefaults: {
                labelRenderer: window.jQuery.jqplot.CanvasAxisLabelRenderer,
            },
            seriesDefaults: {
                showMarker: false,
                shadow: false,
                rendererOptions: {
                    smooth: true,
                },
            },
            highlighter: {
                show: true,
                useAxesFormatters: true,
            },
            series: selfRef.series,
            axes: {
                xaxis: {
                    renderer: window.jQuery.jqplot.LogAxisRenderer,
                    min: start,
                    max: end,
                    label: intl.t('chart_labels.frequency'),
                    rendererOptions: {
                        base: 10,
                    },
                    ticks: xAxisValues,
                    tickOptions: {
                        formatter: selfRef.prettyTickFormatter,
                        alignTicks: true,
                    },
                    pad: 0,
                },
                yaxis: {
                    label: yTitle,
                    tickOptions: {
                        formatter: selfRef.prettyTickFormatter,
                    },
                    min: selfRef.fixedMinY[selfRef.type],
                    max: selfRef.fixedMaxY[selfRef.type],
                    ticks: selfRef.fixedTicks[selfRef.type],
                    pad: 0,
                },
            },
            cursor: {
                show: true,
                showTooltip: true,
            },
            grid: {
                borderColor: '#E5E5E5',
                shadow: false,
                background: '#FFFFFF',
            },
            legend: {
                show: true,
                location: plotData.legendLocation,
                placement: 'insideGrid',
                labels: selfRef.labels,
            },
            seriesColors: selfRef.seriesColors,
        });

        let eventCanvasElement = document.querySelector(`#${this.chartId} .jqplot-event-canvas`);
        if (eventCanvasElement)
            eventCanvasElement.setAttribute(Consts.QA_ATTR_NAME, `${Consts.QA_ATTR_PREFIX}_${this.chartType}_graph`);

        let resizeTimeout;
        const onResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                if (plot && chartElement && chartElement.offsetWidth > 0) {
                    plot.replot({ resetAxes: true }); // Re-draw the plot
                }
            }, 100);
        };
        window.addEventListener('resize', onResize);
    }
}
